import { api } from "@/lib/apiClient";
import type { PaymentMethodDraft } from "@/components/sales/PaymentMethodFields";
import {
  emptyPaymentMethodDraft,
  paymentMethodUsesBank,
} from "@/components/sales/PaymentMethodFields";

export type VoucherCounterpartyType = "customer" | "supplier" | "investor";

export type VoucherTypeOption = {
  id: number;
  code: string;
  title: string;
  nature: string | null;
};

export type VoucherLineDraft = {
  key: string;
  chartOfAccountsId: string;
  accountCode: string;
  accountName: string;
  debit: string;
  credit: string;
  description: string;
  partyType: VoucherCounterpartyType | "";
  partyId: string;
};

export type ManualVoucherDraft = {
  date: string;
  description: string;
  voucherTypeCode: string;
  payment: PaymentMethodDraft;
  lines: VoucherLineDraft[];
};

export type ManualVoucherRecord = {
  id: string;
  date: string;
  description: string;
  totalDebit: number;
  totalCredit: number;
  voucherNumber: string;
  voucherTypeCode: string;
  voucherTypeTitle: string;
  journalEntryId: string;
  createdAt: string;
};

type VoucherApiRecord = {
  id?: number | string;
  date?: string | null;
  description?: string | null;
  total_debit?: number | string;
  total_credit?: number | string;
  voucher_number?: string | null;
  voucher_type_code?: string | null;
  voucher_type_title?: string | null;
  journal_entry_id?: number | string | null;
  created_at?: string | null;
};

type VoucherTypeApiRecord = {
  id?: number | string;
  code?: string | null;
  title?: string | null;
  nature?: string | null;
};

/** Known counterparty control account numbers (mirrors config/accounting.php). */
const COUNTERPARTY_ACCOUNT_BY_NUMBER: Record<string, VoucherCounterpartyType> = {
  "130501": "customer",
  "220701": "supplier",
  "220601": "investor",
  "220801": "investor",
};

function mapVoucher(row: VoucherApiRecord): ManualVoucherRecord {
  return {
    id: String(row.id ?? ""),
    date: row.date ? String(row.date).slice(0, 10) : "",
    description: row.description != null ? String(row.description) : "",
    totalDebit: Number(row.total_debit ?? 0),
    totalCredit: Number(row.total_credit ?? 0),
    voucherNumber: row.voucher_number != null ? String(row.voucher_number) : "",
    voucherTypeCode: row.voucher_type_code != null ? String(row.voucher_type_code) : "",
    voucherTypeTitle: row.voucher_type_title != null ? String(row.voucher_type_title) : "",
    journalEntryId: row.journal_entry_id != null ? String(row.journal_entry_id) : "",
    createdAt: row.created_at != null ? String(row.created_at) : "",
  };
}

export function suggestedCounterpartyTypeForAccount(
  code: string,
  name: string,
): VoucherCounterpartyType | null {
  const number = code.trim();
  if (number && COUNTERPARTY_ACCOUNT_BY_NUMBER[number]) {
    return COUNTERPARTY_ACCOUNT_BY_NUMBER[number];
  }

  const n = name.trim().toLowerCase();
  if (!n) return null;

  if (n.includes("receivable") || n.includes("customer")) return "customer";
  if (
    n.includes("supplier") ||
    (n.includes("payable") &&
      !n.includes("investor") &&
      !n.includes("partner") &&
      !n.includes("wages") &&
      !n.includes("salaries") &&
      !n.includes("employee")) ||
    (n.includes("advance") && n.includes("supplier"))
  ) {
    return "supplier";
  }
  if (n.includes("investor") || n.includes("deal partner") || n.includes("partners")) {
    return "investor";
  }

  return null;
}

/** @deprecated use suggestedCounterpartyTypeForAccount */
export function suggestedPartyRoleForAccount(code: string, name: string) {
  return suggestedCounterpartyTypeForAccount(code, name);
}

export function voucherTypeUsesPayment(nature: string | null | undefined, code: string): boolean {
  const n = (nature ?? "").toLowerCase();
  if (n === "payment" || n === "receipt") return true;
  const c = code.toUpperCase();
  return c === "CPV" || c === "BPV" || c === "CRV" || c === "BRV";
}

export function emptyVoucherLine(): VoucherLineDraft {
  return {
    key: `line-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    chartOfAccountsId: "",
    accountCode: "",
    accountName: "",
    debit: "",
    credit: "",
    description: "",
    partyType: "",
    partyId: "",
  };
}

export function emptyVoucherDraft(date = "", voucherTypeCode = "JV"): ManualVoucherDraft {
  return {
    date,
    description: "",
    voucherTypeCode,
    payment: emptyPaymentMethodDraft(),
    lines: [emptyVoucherLine(), emptyVoucherLine()],
  };
}

export async function listVoucherTypes(): Promise<VoucherTypeOption[]> {
  const { data } = await api.get<{ data?: VoucherTypeApiRecord[] }>("/api/v1/accounts/voucher-types");
  return (data.data ?? [])
    .map((row) => ({
      id: Number(row.id ?? 0),
      code: String(row.code ?? "").toUpperCase(),
      title: String(row.title ?? row.code ?? ""),
      nature: row.nature != null ? String(row.nature) : null,
    }))
    .filter((row) => row.code !== "");
}

export async function listManualVouchers(
  page = 1,
  perPage = 20,
  filters: { dateFrom?: string | null; dateTo?: string | null; voucherTypeCode?: string | null } = {},
): Promise<{
  items: ManualVoucherRecord[];
  meta: { currentPage: number; lastPage: number; perPage: number; total: number };
}> {
  const { data } = await api.get<{
    data?: VoucherApiRecord[];
    meta?: { current_page?: number; last_page?: number; per_page?: number; total?: number };
  }>("/api/v1/accounts/vouchers", {
    params: {
      page,
      per_page: perPage,
      date_from: filters.dateFrom || undefined,
      date_to: filters.dateTo || undefined,
      voucher_type_code: filters.voucherTypeCode || undefined,
    },
  });

  return {
    items: (data.data ?? []).map(mapVoucher),
    meta: {
      currentPage: Number(data.meta?.current_page ?? 1),
      lastPage: Number(data.meta?.last_page ?? 1),
      perPage: Number(data.meta?.per_page ?? perPage),
      total: Number(data.meta?.total ?? 0),
    },
  };
}

export async function createManualVoucher(draft: ManualVoucherDraft): Promise<ManualVoucherRecord> {
  const lines = draft.lines
    .map((line) => ({
      chart_of_accounts_id: Number(line.chartOfAccountsId),
      debit: line.debit === "" ? 0 : Number(line.debit),
      credit: line.credit === "" ? 0 : Number(line.credit),
      description: line.description.trim() || null,
      party_id: line.partyId ? Number(line.partyId) : null,
      party_type: line.partyType || null,
    }))
    .filter((line) => line.chart_of_accounts_id > 0 && (line.debit > 0 || line.credit > 0));

  const payment = draft.payment;
  const method = payment.paymentMethod || null;
  const usesBank = method ? paymentMethodUsesBank(method) : false;

  const { data } = await api.post<{ data: VoucherApiRecord }>("/api/v1/accounts/vouchers", {
    date: draft.date,
    description: draft.description.trim() || null,
    voucher_type_code: draft.voucherTypeCode,
    payment_method: method || null,
    bank_id: usesBank && payment.bankId ? Number(payment.bankId) : null,
    cheque_no: method === "cheque" ? payment.chequeNo?.trim() || null : null,
    pay_order_no: method === "pay_order" ? payment.payOrderNo.trim() || null : null,
    transaction_reference_no:
      method === "online" || method === "cash_deposit"
        ? payment.transactionReferenceNo.trim() || null
        : null,
    deposit_slip_no: method === "cash_deposit" ? payment.depositSlipNo?.trim() || null : null,
    cheque_date: method === "cheque" ? payment.chequeDate || null : null,
    lines,
  });

  return mapVoucher(data.data);
}

export async function deleteManualVoucher(id: string): Promise<void> {
  await api.delete(`/api/v1/accounts/vouchers/${id}`);
}
